/**
 * rateMotion.js — every number that decides how the /rate pages MOVE.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THIS FILE EXISTS TO BE EDITED DURING A TUNING PASS, exactly like
 * clipLook.js and meterLook.js. How fast a card should land is a judgement made
 * looking at the page on a phone, not a value anybody can derive, so the dials
 * live here together rather than scattered through six components: one file
 * open, save, look, save again.
 *
 * 🔴 NOTHING HERE CHANGES A NUMBER, ONLY ITS ARRIVAL. Every grade, percentile,
 * dollar and share on these pages is computed elsewhere and is untouched by
 * everything below. A bad dial here makes the page ugly; it cannot make it
 * report something false.
 *
 * 🔴 REDUCED MOTION IGNORES ALL OF IT. Under prefers-reduced-motion every one
 * of these surfaces paints its FINISHED state on the first frame — no
 * animation, no transition, no count-up. That path must stay reachable no
 * matter what these are set to, and rateMotion.test.js pins it surface by
 * surface.
 * ══════════════════════════════════════════════════════════════════════
 */
export const RATE_MOTION = {
  /**
   * The league page's team cards enter one after another, best team first.
   *
   * ⚠️ THE ARITHMETIC THESE THREE OWE EACH OTHER: the last card starts at
   * min(stagger × (n − 1), cap) and takes riseMs to land, so the whole room
   * finishes at cap + riseMs regardless of size. At the shipped dials that is
   * 600 + 260 = 860ms, which keeps a 14-team room under a second — the point of
   * the cap. Raise the cap and check that sum; nothing enforces it, and a
   * fourteenth card still sliding in two seconds after the first reads as a
   * page that has not finished loading rather than as a flourish.
   */
  cardStaggerMs: 60,
  cardStaggerCapMs: 600,
  cardRiseMs: 260,

  /** How far a card travels on its way in. Small on purpose: this is a lift, not a slide. */
  cardLiftPx: 8,

  /**
   * The grade circle's pop, timed to land WITH its own card rather than after
   * it — the letter is the thing the eye goes to, so it arrives as the card
   * settles instead of announcing itself a beat later.
   */
  gradePopMs: 280,

  /** Position chips, left to right, after the card they sit on. */
  chipStaggerMs: 45,
  chipRiseMs: 220,

  /** The spend bar growing out of its left edge. */
  barFillMs: 520,

  /** Anything revealed by a tap: the picks expansion and a row's price detail. */
  expandMs: 200,

  /**
   * How long the team report's headline points take to count up.
   *
   * Much shorter than METER_LOOK.sweepMs, and deliberately: the meter's slow
   * climb is the moment that page earns, so a headline racing it would pull the
   * eye off the corpus. This one is done before the meter is a third of the way.
   */
  countUpMs: 600,

  /**
   * The curve everything here shares.
   *
   * ⚠️ THE COUNT-UP MIRRORS THIS IN JS, it does not read it — a rAF loop cannot
   * consume a CSS timing function. useCountUp applies a cubic ease-out, which is
   * the same shape as this bezier. Same split, same reason, as meterLook.js.
   */
  easing: "cubic-bezier(0.22, 1, 0.36, 1)",
};
